import { inject, Injectable } from '@angular/core';
import { CampaignAnalyticsService } from '@core/services/campaign-analytics.service';
import { ICampaignAnalyticsApiResponse } from '@interfaces/campaign-analytics-api.interface';
import { ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { ComponentStore } from '@ngrx/component-store';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { catchError, EMPTY, forkJoin, Observable, switchMap, tap } from 'rxjs';

interface IDashboardState {
  campaignAnalyticsMap: Record<string, ICampaignAnalytics>;
  error: string | null;
  loading: boolean;
  loadingAnalytics: boolean;
}

const initialState: IDashboardState = {
  campaignAnalyticsMap: {},
  error: null,
  loading: false,
  loadingAnalytics: false,
};

@Injectable()
export class DashboardStore extends ComponentStore<IDashboardState> {
  public readonly campaignAnalyticsMap$ = this.selectSignal((state: IDashboardState) => state.campaignAnalyticsMap);
  public readonly loading$ = this.selectSignal((state: IDashboardState) => state.loading);
  public readonly loadingAnalytics$ = this.selectSignal((state: IDashboardState) => state.loadingAnalytics);
  public readonly error$ = this.selectSignal((state: IDashboardState) => state.error);

  public readonly loadCampaignAnalytics = this.effect((params$: Observable<{ campaigns: TCampaignApi[] }>) => {
    return params$.pipe(
      tap(() => this.patchState({ loadingAnalytics: true })),
      switchMap(({ campaigns }) => {
        const campaignIds = campaigns
          .map((c: TCampaignApi) => c.id)
          .filter((id: string) => !this.get().campaignAnalyticsMap[id]);

        if (campaignIds.length === 0) {
          this.patchState({ loadingAnalytics: false });

          return EMPTY;
        }

        const batches: string[][] = this.chunkArray(campaignIds, 20);
        const batchRequests = batches.map((batch: string[]) => this.analyticsService.getBulkCampaignAnalytics(batch));

        return forkJoin(batchRequests).pipe(
          tap((batchResponses: ICampaignAnalyticsApiResponse[][]) => {
            const currentMap = { ...this.get().campaignAnalyticsMap };
            const allResponses: ICampaignAnalyticsApiResponse[] = batchResponses.flat();

            allResponses.forEach((response: ICampaignAnalyticsApiResponse) => {
              if (response.campaignAnalytics) {
                const campaign = campaigns.find((c: TCampaignApi) => c.id === response.campaignAnalytics.id);

                if (campaign) {
                  const [analytics] = this.analyticsUtilities.getCampaignAnalyticsData(
                    campaign,
                    response.campaignAnalytics,
                  );
                  currentMap[response.campaignAnalytics.id] = analytics;
                }
              }
            });

            this.patchState({
              campaignAnalyticsMap: currentMap,
              loadingAnalytics: false,
            });
          }),
          catchError(() => {
            this.patchState({ loadingAnalytics: false });

            return EMPTY;
          }),
        );
      }),
    );
  });

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];

    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }

    return chunks;
  }

  private readonly analyticsService = inject(CampaignAnalyticsService);
  private readonly analyticsUtilities = inject(CampaignAnalyticsUtilitiesService);

  constructor() {
    super(initialState);
  }
}
